import { list, object, primitive, serializable } from 'serializr';
import { WorkOrderStatus } from '../enums/workOrderStatus';
import { PaginationMeta } from './userManager/userRecord.model';

export class WorkOrderAsset {
  @serializable(primitive())
  layerId?: number;

  @serializable(primitive())
  layerName?: string;

  @serializable(primitive())
  keyValue?: string;

  @serializable(primitive())
  displayValue?: string;

  @serializable(primitive())
  iconUrl?: string | null;

  @serializable(primitive())
  exists?: boolean;
}

export class WorkOrderContact {
  @serializable(primitive())
  id!: number;

  @serializable(primitive())
  name?: string | null;

  @serializable(primitive())
  email?: string | null;

  @serializable(primitive())
  phone?: string | null;
}

export class WorkOrderUser {
  @serializable(primitive())
  id!: number;

  @serializable(primitive())
  name?: string;
}

export class WorkOrderAttachment {
  @serializable(primitive())
  id!: number;

  @serializable(primitive())
  fileName?: string;

  // The attachment's File Manager file; null for ones attached before filing.
  @serializable(primitive())
  fileId?: string | null;

  @serializable(primitive())
  contentType?: string;

  @serializable(primitive())
  sizeBytes?: number;

  @serializable(primitive())
  url?: string;

  @serializable(primitive())
  uploadedAt?: string;
}

export class WorkOrderListItem {
  @serializable(primitive())
  id!: number;

  @serializable(primitive())
  title?: string;

  // Sequential per asset map; a blank title is saved as "WO <number>".
  @serializable(primitive())
  number?: number;

  @serializable(list(object(WorkOrderAsset)))
  assets?: WorkOrderAsset[];

  // True when any asset on the order has been deleted.
  @serializable(primitive())
  hasDeletedAsset?: boolean;

  @serializable(list(primitive()))
  scheduleDates?: string[];

  @serializable(list(object(WorkOrderContact)))
  contacts?: WorkOrderContact[];

  @serializable(primitive())
  assigneeName?: string | null;

  @serializable(primitive())
  status?: WorkOrderStatus;

  // "YYYY-MM-DD" while the order is COMPLETED.
  @serializable(primitive())
  completedAt?: string | null;

  @serializable(object(WorkOrderUser))
  createdBy?: WorkOrderUser | null;

  @serializable(primitive())
  createdAt?: string;

  @serializable(primitive())
  updatedAt?: string;

  @serializable(primitive())
  attachmentCount?: number;
}

export class WorkOrderDetails {
  @serializable(primitive())
  id!: number;

  @serializable(primitive())
  title?: string;

  @serializable(primitive())
  description?: string;

  // Sequential per asset map; a blank title is saved as "WO <number>".
  @serializable(primitive())
  number?: number;

  @serializable(list(object(WorkOrderAsset)))
  assets?: WorkOrderAsset[];

  // True when any asset on the order has been deleted.
  @serializable(primitive())
  hasDeletedAsset?: boolean;

  @serializable(list(primitive()))
  scheduleDates?: string[];

  @serializable(list(object(WorkOrderContact)))
  contacts?: WorkOrderContact[];

  @serializable(primitive())
  assigneeName?: string | null;

  @serializable(primitive())
  additionalNotes?: string | null;

  @serializable(primitive())
  status?: WorkOrderStatus;

  @serializable(list(object(WorkOrderAttachment)))
  attachments?: WorkOrderAttachment[];

  @serializable(object(WorkOrderUser))
  createdBy?: WorkOrderUser | null;

  @serializable(object(WorkOrderUser))
  updatedBy?: WorkOrderUser | null;

  @serializable(primitive())
  createdAt?: string;

  @serializable(primitive())
  updatedAt?: string;
}

export class WorkOrderCounts {
  @serializable(primitive())
  SCHEDULED?: number;

  @serializable(primitive())
  SUBMITTED?: number;

  @serializable(primitive())
  COMPLETED?: number;

  @serializable(primitive())
  ON_HOLD?: number;

  @serializable(primitive())
  ARCHIVED?: number;
}

export class WorkOrdersResponse {
  @serializable(list(object(WorkOrderListItem)))
  workOrders?: WorkOrderListItem[];

  @serializable(object(WorkOrderCounts))
  counts?: WorkOrderCounts;

  @serializable(object(PaginationMeta))
  pagination?: PaginationMeta;
}

export interface WorkOrdersParams {
  status?: WorkOrderStatus[];
  layerId?: number;
  keyValue?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

// Exactly one is set: `key` for a file uploaded from the device, `fileId` for a File Manager file.
export class WorkOrderAttachmentPayload {
  @serializable(primitive())
  key?: string;

  @serializable(primitive())
  fileId?: string;
}

export class WorkOrderAssetPayload {
  @serializable(primitive())
  layerId!: number;

  @serializable(primitive())
  keyValue!: string;
}

// A blank title gets the next "WO n"; scheduleDates (YYYY-MM-DD) are optional and decide the status.
// An edit replaces assets and contacts. Pass '' for assigneeName/additionalNotes to clear them.
export class WorkOrderPayload {
  @serializable(primitive())
  title?: string;

  @serializable(primitive())
  description?: string;

  @serializable(list(object(WorkOrderAssetPayload)))
  assets?: WorkOrderAssetPayload[];

  @serializable(list(primitive()))
  scheduleDates?: string[];

  @serializable(list(primitive()))
  contactIds?: number[];

  @serializable(primitive())
  assigneeName?: string;

  @serializable(primitive())
  additionalNotes?: string;

  // New attachments only.
  @serializable(list(object(WorkOrderAttachmentPayload)))
  attachments?: WorkOrderAttachmentPayload[];
}

export interface EditWorkOrderPayload {
  id: number;
  data: WorkOrderPayload;
}

// Dates as "YYYY-MM-DD": new schedule dates for SCHEDULED, the completion day for COMPLETED.
export interface UpdateWorkOrderStatusPayload {
  id: number;
  status: WorkOrderStatus;
  scheduleDates?: string[];
  completedAt?: string;
}

export interface UpdateWorkOrderStatusResponse {
  id: number;
  status: WorkOrderStatus;
  updatedAt: string;
  message: string;
}

export interface DeleteWorkOrderResponse {
  id: number;
  message: string;
}

export interface DeleteWorkOrderAttachmentPayload {
  id: number;
  attachmentId: number;
}

export interface DeleteWorkOrderAttachmentResponse {
  id: number;
  attachmentId: number;
  message: string;
}
